import React, { useEffect } from 'react';
import { Save, FolderOpen, Trash2, Cloud, Loader2 } from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import { useProjects } from '../hooks/useProjects';
import useEditorStore from '../store/useEditorStore';
import { snapshotFromState, openExistingProjectInTab, setActiveTabProjectId, getActiveTab } from '../store/tabsManager';
import { toast } from 'sonner';

export default function ProjectsPanel() {
  const { user, loading: authLoading } = useAuth();
  const { projects, loading, list, create, update, get, remove } = useProjects();
  const meta = useEditorStore((s) => s.meta);
  const activeTabId = useEditorStore((s) => s.activeTabId);
  const tabs = useEditorStore((s) => s.tabs);
  const activeTab = tabs.find((t) => t.tabId === activeTabId);
  const currentProjectId = activeTab?.projectId || null;

  useEffect(() => {
    if (user) list().catch((e) => toast.error('Impossible de charger les projets: ' + e.message));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  if (authLoading) return null;
  if (!user) {
    return (
      <div className="panel-card">
        <h3><Cloud size={14} /> Mes projets</h3>
        <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
          Connectez-vous via Google pour sauvegarder vos projets en ligne et les retrouver depuis n'importe quel appareil.
        </div>
      </div>
    );
  }

  const handleSave = async () => {
    try {
      const state = useEditorStore.getState();
      const snap = snapshotFromState(state);
      const projectName = meta?.name?.trim() || 'Sans titre';
      if (currentProjectId) {
        const saved = await update(currentProjectId, { name: projectName, state: snap });
        setActiveTabProjectId(saved.project_id, snap.meta);
        toast.success(`Projet "${projectName}" mis à jour`);
      } else {
        const proj = await create({ name: projectName, state: snap });
        setActiveTabProjectId(proj.project_id, snap.meta);
        toast.success(`Projet "${projectName}" enregistré`);
      }
    } catch (e) {
      toast.error('Erreur sauvegarde: ' + e.message);
    }
  };

  const handleLoad = async (p) => {
    try {
      const full = await get(p.project_id);
      // Open in a tab (reuse existing tab if same project already open)
      openExistingProjectInTab(full);
      toast.success(`Projet "${p.name}" chargé`);
    } catch (e) {
      toast.error('Erreur chargement: ' + e.message);
    }
  };

  const handleDelete = async (p) => {
    if (!window.confirm(`Supprimer le projet "${p.name}" ?`)) return;
    try {
      await remove(p.project_id);
      toast.success('Projet supprimé');
    } catch (e) {
      toast.error('Erreur suppression: ' + e.message);
    }
  };

  return (
    <div className="panel-card">
      <h3><Cloud size={14} /> Mes projets <span style={{ marginLeft: 'auto', fontSize: 11, color: 'var(--text-muted)' }}>{projects.length}</span></h3>
      <div className="save-row">
        <button className="btn-secondary" onClick={handleSave} title="Sauvegarder" data-testid="project-save" style={{ width: '100%', justifyContent: 'center' }}>
          <Save size={14} /> {currentProjectId ? 'Enregistrer les modifications' : 'Enregistrer ce projet'}
        </button>
      </div>
      {loading && <div style={{ fontSize: 12, color: 'var(--text-muted)', padding: '8px 0' }}><Loader2 size={12} className="spin-anim" /> Chargement…</div>}
      <div className="projects-list" data-testid="projects-list">
        {projects.map((p) => (
          <div
            key={p.project_id}
            className={`project-row ${currentProjectId === p.project_id ? 'active' : ''}`}
            data-testid={`project-${p.project_id}`}
          >
            <FolderOpen size={13} />
            <div className="project-info" onClick={() => handleLoad(p)}>
              <div className="project-name">{p.name}</div>
              <div className="project-date">
                {p.state?.meta?.client ? `${p.state.meta.client} · ` : ''}
                {new Date(p.updated_at).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' })}
              </div>
            </div>
            <button className="icon-mini danger" onClick={() => handleDelete(p)} title="Supprimer">
              <Trash2 size={11} />
            </button>
          </div>
        ))}
        {!loading && projects.length === 0 && (
          <div style={{ fontSize: 12, color: 'var(--text-muted)', textAlign: 'center', padding: 16 }}>
            Aucun projet — enregistrez votre configuration actuelle
          </div>
        )}
      </div>
    </div>
  );
}
